import { action } from 'mobx';
import { ClassesService, SeasonService } from 'modules/content';
import ModuleUtil from 'modules/content/moduleService/ModuleUtil';
import MembershipService from 'modules/membership/MembershipService';
import { UiService } from 'modules/ui';

import Store from './ContentStore';
import { generateId } from './helper';

import Request from '@hotmart/request';

/**
 * @class
 * This class manages all operations related to Content, including
 * API requests and store management.
 * All required data (including dependency on data from other services) and its
 * own core data fetching should be dealt with in the `init` method.
 * It current depends on:
 * - Membership data, from `MembershipService`;
 * - Season data, from `SeasonService`;
 */
class ContentService {
  wasInitialized = false;

  async init() {
    const wasInitialized =
      this.wasInitialized &&
      Store.lastLoadedProductId === MembershipService.productId;

    if (!wasInitialized) {
      if (MembershipService.hasSeason) {
        await SeasonService.init();
      }

      this.clear();
      this.wasInitialized = true;
    }
  }

  @action
  clear() {
    Object.assign(Store, {
      loaded: false,
      loadedExtra: false,
      modulesList: [],
      modulesExtraList: []
    });
  }

  @action
  setPrincipalData(modulesList = []) {
    const modulesExtraList = Store.modulesExtraList;

    this.setData(modulesList, modulesExtraList);
  }

  @action
  setExtraData(modulesExtraList = []) {
    const modulesList = Store.modulesList;

    this.setData(modulesList, modulesExtraList);
  }

  @action
  setData(modulesList = [], modulesExtraList = []) {
    Object.assign(Store, {
      modulesList: [...modulesList],
      modulesExtraList: [...modulesExtraList]
    });
  }

  @action
  setModulePages(moduleId, pagesList = {}, isExtra = false) {
    const modulesList = Store.modulesList;
    const modulesExtraList = Store.modulesExtraList;
    const index = isExtra
      ? modulesExtraList.findIndex((element) => element.id === moduleId)
      : modulesList.findIndex((element) => element.id === moduleId);

    if (index > -1) {
      if (isExtra) {
        modulesExtraList[index].pages = pagesList;
        modulesExtraList[index].loadedPages = true;
        modulesExtraList[index].totalPages = pagesList.length;
      } else {
        modulesList[index].pages = pagesList;
        modulesList[index].loadedPages = true;
        modulesList[index].totalPages = pagesList.length;
      }
      this.setData(modulesList, modulesExtraList);
      this.setAllModulesHaveLoadedPages(isExtra);
    }
  }

  @action
  setModuleClassesInfo(moduleIndex, classesInfo = {}, isExtra = false) {
    const modulesList = Store.modulesList;
    const modulesExtraList = Store.modulesExtraList;

    if (isExtra) {
      modulesExtraList[moduleIndex].classesInfo = classesInfo;
    } else {
      modulesList[moduleIndex].classesInfo = classesInfo;
    }
    this.setData(modulesList, modulesExtraList);
  }

  insertContent(data) {
    if (data) {
      data.id = generateId();
      Store.modulesList.push(data);
    }
  }

  updateContent(data) {
    const modulesList = Store.modulesList.map((item) => {
      if (item.id === data.id) {
        item = { ...item, ...data };
      }

      return item;
    });

    Store.modulesList = modulesList;
  }

  updateModuleName(moduleId, newName) {
    return Request('module.rename', { id: moduleId, name: newName });
  }

  updatePageName(pageId, newName) {
    return Request('page.rename', { id: pageId, name: newName });
  }

  reOrderModule(id, targetModuleOrderId) {
    return Request('module.sort', { id, targetModuleOrderId });
  }

  reOrderPage(id, targetPageOrderId, newModuleId) {
    return Request('page.sort', { id, targetPageOrderId, newModuleId });
  }

  getModule(id) {
    return Store.modulesList.find((item) => item.id === id);
  }

  async getPagesList(modulesList, index, isExtra, moduleId, force = false) {
    let pagesList = [];

    if (
      force ||
      (!modulesList[index].loadedPages && !modulesList[index].requestedPages)
    ) {
      if (isExtra) {
        Store.modulesExtraList[index].requestedPages = true;
      } else {
        Store.modulesList[index].requestedPages = true;
      }

      pagesList = await Request('module.getPages', { id: moduleId });

      this.setModulePages(moduleId, pagesList, isExtra);
    } else {
      pagesList = modulesList[index].pages || [];
    }

    return pagesList;
  }

  @action
  async getDuplicationPages(id) {
    try {
      const response = await Request('duplication.candidates', { id });

      return response;
    } catch (error) {
      return false;
    }
  }

  @action
  async duplicate(params) {
    try {
      const response = await Request('duplicate.module', params);

      return response;
    } catch (error) {
      return false;
    }
  }

  @action
  async duplicatePage(params) {
    try {
      const response = await Request('duplicate.page', params);

      return response;
    } catch (error) {
      return false;
    }
  }

  get moduleId() {
    return Store.page.moduleId;
  }

  @action
  fetchMainModules = async ({ seasonId }) => {
    this.cachedMainModules = [];

    try {
      this.cachedMainModules = await Request('module.getAll', {
        seasonId,
        extra: false
      });
    } catch (error) {
      UiService.handleError(error);
    }

    const modules = ModuleUtil.filterByName(
      this.cachedMainModules,
      Store.search
    );

    Object.assign(Store, {
      modulesList: modules,
      loaded: true,
      lastLoadedProductId: MembershipService.productId
    });

    return modules;
  };

  @action
  fetchExtraModules = async ({ seasonId }) => {
    this.cachedExtraModules = [];

    try {
      this.cachedExtraModules = await Request('module.getAll', {
        seasonId,
        extra: true
      });
    } catch (error) {
      UiService.handleError(error);
    }

    const modules = ModuleUtil.filterByName(
      this.cachedExtraModules,
      Store.search
    );

    Object.assign(Store, {
      modulesExtraList: modules,
      loadedExtra: true,
      lastLoadedProductId: MembershipService.productId
    });

    return modules;
  };

  @action
  filterMainModules = async (search) => {
    Store.search = search;

    const modules = ModuleUtil.filterByName(
      this.cachedMainModules || [],
      search
    );

    Object.assign(Store, { modulesList: modules });

    return modules;
  };

  @action
  filterExtraModules = async (search) => {
    Store.search = search;

    const modules = ModuleUtil.filterByName(
      this.cachedExtraModules || [],
      search
    );

    Object.assign(Store, { modulesExtraList: modules });

    return modules;
  };

  /** @type (seasonId: string | string[] | null, extra: boolean) => Promise<never> */
  fetchModulesList(seasonId, extra = false) {
    if (!seasonId && MembershipService.hasSeason) {
      seasonId = SeasonService.getSelectedSeasonInfo()?.id || null;
    }

    if (extra) {
      return this.fetchExtraModules({ seasonId });
    }

    return this.fetchMainModules({ seasonId });
  }

  @action
  async fetchModulePagesList(moduleId, isExtra = false, force = false) {
    let pagesList = [];
    const modulesList = isExtra ? Store.modulesExtraList : Store.modulesList;
    const index = modulesList.findIndex((element) => element.id === moduleId);

    if (index > -1) {
      try {
        pagesList = await this.getPagesList(
          modulesList,
          index,
          isExtra,
          moduleId,
          force
        );
      } catch (error) {
        UiService.handleError(error);
      }
    }

    return pagesList;
  }

  @action
  async getModuleClassesInfo(moduleId, isExtra = false) {
    const modulesList = Store.modulesList;
    const modulesExtraList = Store.modulesExtraList;
    const index = isExtra
      ? modulesExtraList.findIndex((element) => element.id === moduleId)
      : modulesList.findIndex((element) => element.id === moduleId);

    if (index > -1) {
      try {
        if (isExtra) {
          if (!modulesExtraList[index].classesInfo) {
            const classesInfos = await ClassesService.getModulesClassesInfos([
              ...modulesExtraList[index].classes
            ]);

            this.setModuleClassesInfo(index, classesInfos, isExtra);
            return classesInfos;
          }
          return modulesExtraList[index].classesInfo;
        } else {
          if (!modulesList[index].classesInfo) {
            const classesInfos = await ClassesService.getModulesClassesInfos([
              ...modulesList[index].classes
            ]);

            this.setModuleClassesInfo(index, classesInfos, isExtra);
            return classesInfos;
          }
          return modulesList[index].classesInfo;
        }
      } catch (error) {
        UiService.handleError(error);
      }
    }
  }

  massDelete(modules, pages) {
    return Request('mass.delete', { modules, pages });
  }

  deleteModule(id) {
    return Request('mass.delete', { modules: [id], pages: [] });
  }

  deletePage(id) {
    return Request('mass.delete', { modules: [], pages: [id] });
  }

  setAllModulesHaveLoadedPages(isExtra = false) {
    if (isExtra && Store.loadedExtra) {
      const modulesFiltered =
        Store.modulesExtraList &&
        Store.modulesExtraList.filter(
          (module) => !module.loadedPages && module.totalPages > 0
        );

      Store.allModulesExtraLoadedPages = modulesFiltered.length === 0;
    } else if (Store.loaded) {
      const modulesFiltered =
        Store.modulesList &&
        Store.modulesList.filter(
          (module) => !module.loadedPages && module.totalPages > 0
        );

      Store.allModulesLoadedPages = modulesFiltered.length === 0;
    }
  }

  set lastEditedModule(moduleId) {
    Store.lastEditedModule = moduleId;
  }

  get lastEditedModule() {
    return Store.lastEditedModule;
  }

  set lastCreatedModule(moduleId) {
    Store.lastCreatedModule = moduleId;
  }

  get lastCreatedModule() {
    return Store.lastCreatedModule;
  }

  @action
  async getfirstModulePaid() {
    try {
      const response = await Request('module.firstModulePaid');

      return response;
    } catch (error) {
      return '';
    }
  }

  async fetchUserLanguages() {
    let response = [];

    try {
      response = await Request('get.userprofile.languages');
      // eslint-disable-next-line no-empty
    } catch {}

    return response;
  }

  findPageById(pageId) {
    let page = null;

    const allModules = (Store.modulesList || []).concat(
      Store.modulesExtraList || []
    );

    allModules.some((module) => {
      page = module.pages?.find((page) => page.id === pageId);
      return Boolean(page);
    });

    return page;
  }
}

export default new ContentService();
